import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  clearMyTimezone,
  readMyTimezones,
  setMyTimezone,
  type MyTimezonesRead,
  type TimezoneOverrideWrite,
} from '@/client'

/**
 * Server state for the caller's time zones: their narrowings per page and
 * store, and the zones of every store they may see. One read serves every
 * page, so it is kept fresh for a while rather than refetched per page.
 *
 * The profile default is DV-Auth's and arrives on the session's principal.
 */
export const timezoneKeys = {
  all: ['timezones'] as const,
  mine: () => [...timezoneKeys.all, 'mine'] as const,
}

export function useMyTimezones(enabled = true) {
  return useQuery({
    queryKey: timezoneKeys.mine(),
    queryFn: async (): Promise<MyTimezonesRead> =>
      (await readMyTimezones({ throwOnError: true })).data,
    staleTime: 5 * 60_000,
    enabled,
  })
}

/** Set a zone for a page and/or a store; the same pair again replaces it. */
export function useSetTimezone() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: TimezoneOverrideWrite) =>
      (await setMyTimezone({ body, throwOnError: true })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timezoneKeys.all }),
  })
}

/** Drop a zone for a page and/or a store, so the page falls back along the chain. */
export function useClearTimezone() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { store_id?: number | null; page?: string | null }) => {
      await clearMyTimezone({
        query: { store_id: params.store_id ?? undefined, page: params.page ?? undefined },
        throwOnError: true,
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timezoneKeys.all }),
  })
}
